import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Trophy,
  Building2,
  Calendar,
  Settings,
  ChevronLeft,
  ChevronRight,
  Link,
  QrCode,
  Receipt,
  Globe,
  Award,
  Menu,
  X,
  Monitor,
  BarChart3,
  BookOpen,
  PlayCircle,
  Send,
  GraduationCap,
  GitMerge,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { SOCIAL_CALENDAR_LIVE } from '../../config/featureFlags';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/tournaments', icon: Trophy, label: 'Tournaments' },
  { to: '/admin/leagues', icon: Award, label: 'Leagues' },
  { to: '/admin/clinics', icon: GraduationCap, label: 'Clinics' },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports' },
  { to: '/admin/event-pages', icon: Globe, label: 'Share Links' },
  { to: '/admin/invite-links', icon: Link, label: 'Invite Links' },
  { to: '/admin/qr-codes', icon: QrCode, label: 'QR Codes' },
  { to: '/admin/invoices', icon: Receipt, label: 'Invoices' },
  { to: '/admin/organizations', icon: Building2, label: 'Organizations' },
  { to: '/admin/duplicate-teams', icon: GitMerge, label: 'Duplicate Teams' },
  { to: '/admin/schedule', icon: Calendar, label: 'Schedule' },
  ...(SOCIAL_CALENDAR_LIVE
    ? [{ to: '/admin/social-calendar', icon: Send, label: 'Social Calendar' } as const]
    : []),
  { to: '/admin/sample-events', icon: PlayCircle, label: 'Sample Events' },
  { to: '/admin/sample-matches', icon: Monitor, label: 'Sample Scoreboards' },
  { to: '/admin/how-to', icon: BookOpen, label: 'How To Guide' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when navigating on mobile
  const handleNavClick = () => {
    if (isMobile) {
      setCollapsed(true);
    }
  };

  return (
    <>
      {/* Mobile hamburger button - fixed position */}
      {isMobile && collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg text-white"
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          <Menu className="w-6 h-6" />
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && !collapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`text-white transition-all duration-300 min-h-screen flex flex-col z-50 ${
          isMobile
            ? `fixed top-0 left-0 h-full w-64 ${collapsed ? '-translate-x-full' : 'translate-x-0'}`
            : collapsed ? 'w-16' : 'w-64'
        }`}
        style={{ backgroundColor: 'var(--color-primary)' }}
      >
        <div 
          className={`p-2 flex items-center ${collapsed && !isMobile ? 'justify-center' : 'justify-between'}`}
          style={{ borderBottom: '1px solid var(--color-primary-dark)' }}
        >
          {(!collapsed || isMobile) && (
            <span className="text-lg font-bold text-white pl-2">Admin Panel</span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-3 rounded-lg transition-colors hover:opacity-80"
            style={{ backgroundColor: 'transparent' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-dark)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {isMobile ? (
              <X className="w-5 h-5" />
            ) : collapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        <nav className="flex-1 p-2 overflow-y-auto">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.to} className={collapsed && !isMobile ? 'flex justify-center' : ''}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={handleNavClick}
                  className={`flex items-center gap-3 rounded-lg transition-colors ${
                    collapsed && !isMobile ? 'p-3 justify-center' : 'px-3 py-2'
                  }`}
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? 'var(--color-secondary)' : 'transparent',
                    color: 'white',
                    opacity: isActive ? 1 : 0.8,
                  })}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {(!collapsed || isMobile) && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div 
          className={`p-2 ${collapsed && !isMobile ? 'flex justify-center' : ''}`}
          style={{ borderTop: '1px solid var(--color-primary-dark)' }}
        >
          <NavLink
            to="/"
            onClick={handleNavClick}
            className={`flex items-center gap-3 rounded-lg transition-colors ${
              collapsed && !isMobile ? 'p-3 justify-center' : 'px-3 py-2'
            }`}
            style={{ color: 'rgba(255, 255, 255, 0.8)' }}
          >
            <Trophy className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || isMobile) && <span>Back to Site</span>}
          </NavLink>
        </div>
      </aside>
    </>
  );
}
